import { type PageId } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { GlassCard } from "@/components/ui/glass-card";
import { fx } from "@/lib/effects";
import { cn } from "@/lib/utils";
import { useState, useRef, useCallback } from "react";
import {
  CircleDot,
  Library,
  Presentation,
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
  ArrowRight,
  Camera,
  Heart,
  MessageCircle,
  ExternalLink,
  Play,
  VolumeX,
  Volume2,
  Film,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { FeedPost } from "@shared/types";

interface StartHerePageProps {
  onNavigate: (page: PageId) => void;
}

/* ── Three pathway cards ──────────────────────────────────────────────────── */
const PATHWAYS: {
  id: PageId;
  icon: React.ElementType;
  label: string;
  description: string;
  cta: string;
  accent: "sage" | "teal" | "gold";
}[] = [
  {
    id: "venn",
    icon: CircleDot,
    label: "Explore the Data",
    description:
      "Three overlapping dimensions — orientation, structure, and ethics — mapped across six demographic profiles on an interactive radar graph.",
    cta: "Open Venn Diagram",
    accent: "sage",
  },
  {
    id: "references",
    icon: Library,
    label: "Read the Research",
    description:
      "Peer-reviewed citations from Gallup, Kinsey Institute, and the Survey Center on American Life — every data point is sourced.",
    cta: "View References",
    accent: "teal",
  },
  {
    id: "pitchdeck",
    icon: Presentation,
    label: "See the Investment Case",
    description:
      "The Lola & Ber hospitality investment overview — how the New Majority thesis translates into a market opportunity.",
    cta: "Open Pitch Deck",
    accent: "gold",
  },
];

/* ── Quick stats strip — all numbers from radarData.ts ────────────────────── */
const STATS: {
  value: string;
  label: string;
  source: string;
  icon: React.ElementType;
}[] = [
  {
    value: "~65%",
    label: "of U.S. adults fall outside at least one traditional norm",
    source: "Composite estimate — Gallup, Kinsey, SCAL",
    icon: Users,
  },
  {
    value: "92%",
    label: "Growth Trend score for the New Majority profile",
    source: "radarData.ts — New Majority profile",
    icon: TrendingUp,
  },
  {
    value: "6 × 6",
    label: "Six profiles measured across six axes",
    source: "Redefining Normal™ radar framework",
    icon: BarChart3,
  },
];
/* ── Mini Instagram Card with inline video ────────────────────────────── */
function MiniInstagramCard({ post }: { post: FeedPost }) {
  const igUrl = `https://www.instagram.com/p/${post.short_code}/`;
  const videoUrl = post.cdn_video_url || post.video_url;
  const isVideo = post.post_type === "Video" && videoUrl;

  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play().catch(() => setVideoError(true));
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  }, []);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(!muted);
  }, [muted]);

  return (
    <GlassCard accent="sage" className="space-y-3 overflow-hidden">
      {post.cdn_image_url && (
        <div className="relative -mx-4 -mt-4 sm:-mx-5 sm:-mt-5">
          {isVideo && !videoError ? (
            <>
              <video
                ref={videoRef}
                src={videoUrl}
                poster={post.cdn_image_url}
                muted={muted}
                playsInline
                loop
                preload="none"
                onClick={togglePlay}
                onError={() => setVideoError(true)}
                className="w-full h-48 object-cover cursor-pointer"
              />
              {!playing && (
                <button
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors cursor-pointer"
                  aria-label="Play video"
                >
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-5 h-5 text-primary ml-0.5" fill="currentColor" />
                  </div>
                </button>
              )}
              {playing && (
                <button
                  onClick={toggleMute}
                  className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/70 transition-colors"
                  aria-label={muted ? "Unmute" : "Mute"}
                >
                  {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
              )}
              {/* Video badge */}
              <div className="absolute top-2 right-2 bg-black/50 backdrop-blur-sm text-white rounded-full px-2 py-0.5 flex items-center gap-1 text-xs">
                <Film className="w-3 h-3" />
                <span>Video</span>
              </div>
            </>
          ) : (
            <a href={igUrl} target="_blank" rel="noopener noreferrer" className="block">
              <img
                src={post.cdn_image_url}
                alt={post.caption?.slice(0, 80) ?? "Instagram post"}
                className="w-full h-48 object-cover"
                loading="lazy"
              />
              {isVideo && videoError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                    <Play className="w-5 h-5 text-primary ml-0.5" fill="currentColor" />
                  </div>
                </div>
              )}
            </a>
          )}
        </div>
      )}
      <p className="text-sm text-foreground line-clamp-3 leading-relaxed">
        {post.caption?.slice(0, 200) ?? ""}
      </p>
      <div className="flex items-center gap-4 text-xs text-muted-foreground font-label">
        {post.likes_count != null && post.likes_count > 0 && (
          <span className="flex items-center gap-1">
            <Heart className="w-3.5 h-3.5" />
            {post.likes_count.toLocaleString()}
          </span>
        )}
        {post.comments_count != null && post.comments_count > 0 && (
          <span className="flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" />
            {post.comments_count.toLocaleString()}
          </span>
        )}
        {post.published_at && (
          <span className="ml-auto">
            {new Date(post.published_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </span>
        )}
      </div>
      <a
        href={igUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline font-label"
      >
        View on Instagram
        <ExternalLink className="w-3 h-3" />
      </a>
    </GlassCard>
  );
}

/* ── Latest from Instagram component ────────────────────────────────────── */function LatestInstagram({ onNavigate }: { onNavigate: (page: PageId) => void }) {
  const { data, isLoading } = trpc.instagram.listPosts.useQuery({
    limit: 6,
    filter: "all",
  });

  const posts = data?.posts ?? [];

  if (isLoading) {
    return (
      <section className="space-y-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">Latest from Instagram</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-72 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (posts.length === 0) return null;

  return (
    <section className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2.5">
          <Camera className="w-6 h-6 text-primary" />
          Latest from Instagram
        </h2>
        <Button
          variant="outline"
          size="sm"
          className="border-primary/30 text-primary hover:bg-primary/10"
          onClick={() => onNavigate("instagram" as PageId)}
        >
          View All
          <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {posts.map((post: FeedPost) => (
          <MiniInstagramCard key={post.short_code} post={post} />
        ))}
      </div>
    </section>
  );
}

export function StartHerePage({ onNavigate }: StartHerePageProps) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14 space-y-12">

        {/* ── Hero ──────────────────────────────────────────────────────── */}
        <section className="space-y-6">
          <div className="flex items-center gap-2.5">
            <img
              src="https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/lb-logo_6a402776.png"
              alt="Lola & Ber"
              className="h-8 w-8 rounded-sm object-contain"
              fetchPriority="high"
              loading="eager"
            />
            <span className="text-sm font-label font-semibold tracking-widest uppercase text-primary">
              A Lola &amp; Ber What If Research Platform
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-foreground leading-tight">
            The majority has already changed.
            <br />
            <span className="text-primary">
              The data just hasn&apos;t been told yet.
            </span>
          </h1>

          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
            When you combine everyone who is non-heterosexual, non-monogamous, or
            who has been unfaithful, the resulting group — the{" "}
            <strong className="text-foreground">New Majority</strong> — is
            estimated at roughly 65% of U.S. adults. The group culture treats as
            the default, the Traditional Norm, accounts for approximately 30%.
            This platform presents the evidence.
          </p>

          <div className="h-1 w-24 rounded-full bg-accent opacity-40" />
        </section>

        {/* ── Quick Stats Strip ────────────────────────────────────────── */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STATS.map((s) => {
            const Icon = s.icon;
            return (
              <GlassCard key={s.value} accent="sage">
                <div className="flex items-start gap-3.5">
                  <div className={cn(
                    "flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0 mt-0.5",
                    "transition-transform duration-200 group-hover:scale-110",
                  )}>
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-2xl font-bold text-foreground leading-tight">
                      {s.value}
                    </p>
                    <p className="text-sm text-muted-foreground leading-snug mt-1">
                      {s.label}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1.5 italic font-label">
                      {s.source}
                    </p>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </section>

        {/* ── Divider ──────────────────────────────────────────────────── */}
        <div className="border-t border-border" />

        {/* ── Three Pathway Cards ──────────────────────────────────────── */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-6">
            Where would you like to start?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PATHWAYS.map((p) => {
              const Icon = p.icon;
              const accentBg = p.accent === "teal" ? "bg-accent-teal/10" : p.accent === "gold" ? "bg-accent-gold/10" : "bg-primary/10";
              const accentText = p.accent === "teal" ? "text-accent-teal" : p.accent === "gold" ? "text-accent-gold" : "text-primary";
              const accentHover = p.accent === "teal" ? "group-hover:text-accent-teal" : p.accent === "gold" ? "group-hover:text-accent-gold" : "group-hover:text-primary";
              return (
                <GlassCard
                  key={p.id}
                  accent={p.accent}
                  onClick={() => onNavigate(p.id)}
                  className="group space-y-4"
                >
                  <div className={cn(
                    "inline-flex items-center justify-center w-10 h-10 rounded-lg",
                    accentBg, accentText,
                    "transition-transform duration-200 group-hover:scale-110 group-hover:rotate-3",
                  )}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="space-y-1.5">
                    <p className={cn("text-base font-semibold text-foreground transition-colors", accentHover)}>
                      {p.label}
                    </p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {p.description}
                    </p>
                  </div>
                  <p className={cn("text-sm font-semibold flex items-center gap-1.5", accentText)}>
                    {p.cta}
                    <ArrowRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                  </p>
                </GlassCard>
              );
            })}
          </div>
        </section>

        {/* ── Divider ──────────────────────────────────────────────────── */}
        <div className="border-t border-border" />

        {/* ── Sage CTA ─────────────────────────────────────────────────── */}
        <section className={cn(
          "flex flex-col sm:flex-row items-start sm:items-center gap-5 p-6 rounded-xl",
          "border border-primary/20 bg-primary/5",
          fx.card,
        )}>
          <div className={cn(
            "flex items-center justify-center w-11 h-11 rounded-full bg-primary/15 shrink-0",
            "transition-transform duration-300",
          )}>
            <MessageSquare className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-foreground">
              Have a question? Ask Sage.
            </p>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              Sage is the research co-pilot for this platform. Ask about any
              data point, concept, or finding — and get a sourced answer
              instantly.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className={cn(
              "shrink-0 border-primary/30 text-primary hover:bg-primary/10",
              fx.cta,
            )}
            onClick={() => {
              window.dispatchEvent(new CustomEvent("sage:open"));
            }}
          >
            <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
            Open Sage
          </Button>
        </section>

        {/* ── Latest from Instagram ──────────────────────────────────────────── */}
        <div className="border-t border-border" />
        <LatestInstagram onNavigate={onNavigate} />

        {/* ── Footer ─────────────────────────────────────────────────────────── */}
        <p className="text-sm text-muted-foreground text-center pb-6 font-label">
          Lola &amp; Ber &nbsp;&middot;&nbsp; What If &nbsp;&middot;&nbsp; &copy; 2026
        </p>
      </div>
    </div>
  );
}
